import {
  faChevronRight,
  faCircleInfo,
  faLeaf
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type ReactNode, useState } from "react";

import { T, useT } from "@repo/transifex";
import { cn } from "~/lib/utils";

import { Co2GradeMark } from "./co2-grade-mark";
import { MacroChip } from "./macro-chip";
import { ServingBasisToggle } from "./serving-basis-toggle";
import {
  getMacroChips,
  hasNutritionValues,
  type CO2Rating,
  type MacroKey,
  type NutritionServingBasis,
  type NutritionalInformation
} from "./nutrition-climate-utils";

const MACRO_LABELS: Record<MacroKey, ReactNode> = {
  energy: <T _str="Kcal" />,
  protein: <T _str="Protein" />,
  carbohydrates: <T _str="Carbs" />,
  fat: <T _str="Fat" />
};

/** Replaces the grade cell when a product has no climate footprint data. */
const Co2NoEstimate = () => (
  <div className="flex shrink-0 flex-col items-center justify-center gap-1.5 rounded-2xl bg-kiosk-background-secondary px-5 py-3.5">
    <div className="flex size-11 items-center justify-center rounded-full bg-kiosk-border-secondary text-kiosk-text-secondary">
      <FontAwesomeIcon icon={faLeaf} className="text-[22px]" />
    </div>
    <p className="whitespace-nowrap text-sm font-semibold text-kiosk-text-secondary">
      <T _str="No estimate" />
    </p>
  </div>
);

/** Replaces the macro chips when nutrition values are missing. */
const NutritionUnavailable = () => (
  <div className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl bg-kiosk-background-secondary px-[18px] py-3.5 text-kiosk-text-secondary">
    <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-kiosk-border-secondary">
      <FontAwesomeIcon icon={faCircleInfo} className="text-[22px]" />
    </div>
    <span className="text-base font-medium">
      <T _str="Nutritional information isn't available" />
    </span>
  </div>
);

type NutritionClimateRowProps = {
  /** Nutrition data. When absent, the macro area shows an empty state. */
  nutrition?: NutritionalInformation | null;
  /**
   * Climate grade A–E (from the offer's environmental rating), passed separately
   * from the nutrition data. When absent, the CO₂ cell shows a "No estimate" state.
   */
  co2Rating?: CO2Rating | null;
  /** Opens the full nutrition table. The chevron is hidden when omitted. */
  onOpenFullNutrition?: () => void;
  className?: string;
};

/**
 * Inline "Nutrition & climate" metric row: a climate grade (CO₂) mark plus four
 * macro chips (calories, protein, carbs, fat), with a chevron to open the full
 * nutrition table. A self-contained toggle switches the macros between per
 * serving and per 100 g (shown only when the serving size is known).
 *
 * Empty states are independent: with no climate data the grade cell becomes a
 * neutral "No estimate" chip (nutrition still shows); with no nutrition the
 * macro chips become an "unavailable" message (the CO₂ grade still shows).
 * Purely presentational — renders nothing when neither is available.
 */
export const NutritionClimateRow = ({
  nutrition,
  co2Rating,
  onOpenFullNutrition,
  className
}: NutritionClimateRowProps) => {
  const [basis, setBasis] = useState<NutritionServingBasis>(
    nutrition?.servingBasis ?? "serving"
  );
  const t = useT();

  const showNutrients = hasNutritionValues(nutrition);

  if (!showNutrients && !co2Rating) return null;

  const canToggle = showNutrients && Boolean(nutrition?.servingSize);

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      <div className="flex items-center justify-between gap-4">
        <p className="text-2xl font-bold text-kiosk-text-primary">
          <T _str="Nutrition & climate" />
        </p>

        {canToggle ? (
          <ServingBasisToggle value={basis} onChange={setBasis} />
        ) : null}
      </div>

      <div className="flex items-stretch gap-2">
        {co2Rating ? <Co2GradeMark rating={co2Rating} /> : <Co2NoEstimate />}

        {showNutrients ? (
          getMacroChips(nutrition, basis).map((macro) => (
            <MacroChip
              key={macro.key}
              value={macro.value}
              unit={macro.unit}
              label={MACRO_LABELS[macro.key]}
            />
          ))
        ) : (
          <NutritionUnavailable />
        )}

        {showNutrients && onOpenFullNutrition ? (
          <button
            type="button"
            onClick={onOpenFullNutrition}
            aria-label={t("Open full nutrition")}
            className="flex w-[66px] shrink-0 items-center justify-center rounded-2xl border border-kiosk-border-secondary bg-kiosk-background-secondary text-kiosk-text-secondary active:bg-kiosk-border-secondary"
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              aria-hidden
              className="text-[26px]"
            />
          </button>
        ) : null}
      </div>
    </div>
  );
};
